/**
 * Settings Page Component
 *
 * Sections:
 * 1. Personal data - Profile, security, preferences, organization
 * 2. Integrations - Connected services
 * 3. Subscription - Plan, team and privileges (admin only)
 */

import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks';
import {
  User,
  Lock,
  Bell,
  Globe,
  Palette,
  Upload,
  Mail,
  Phone,
  MapPin,
  Shield,
  Key,
  Smartphone,
  Link as LinkIcon,
  Check,
  X,
  Settings as SettingsIcon,
  Building2,
  Camera,
  Save,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  CreditCard,
  Users,
  UserPlus,
  Crown,
  Eye,
  MessageSquare,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronRight,
  Download,
  FileJson,
  Database,
  FolderSync,
  Play,
  Square,
  FileCheck,
  Loader2,
  ExternalLink,
  HardDrive,
  CalendarDays,
  FolderKanban,
  FileText,
  Table,
  Video,
  LogOut,
} from 'lucide-react';
import { integrations, getCurrentUserSettings, useGoogleConnection, graphqlMutation, graphqlQuery, uploadImage, getPublicFileUrl } from '@business-assistant/shared';
import { GoogleConnectButton, GoogleServiceGrid } from './integrations';
import { useAuth } from '../contexts/AuthContext';
import { ReportIssueSection, MyTicketsSection, SupportSettings, AccountsManagement } from './settings';
import './SettingsPage.css';

// GraphQL mutations for email integration
const SAVE_EMAIL_INTEGRATION = `
  mutation SaveEmailIntegration($input: EmailIntegrationInput!, $userId: String) {
    saveEmailIntegration(input: $input, userId: $userId) {
      id
      provider
      email
      status
      imapHost
      imapPort
      smtpHost
      smtpPort
    }
  }
`;

const GET_EMAIL_INTEGRATIONS = `
  query GetEmailIntegrations($userId: String) {
    emailIntegrations(userId: $userId) {
      id
      provider
      email
      status
      imapHost
      imapPort
      imapTls
      smtpHost
      smtpPort
      smtpSecure
      syncEnabled
    }
  }
`;

const DELETE_EMAIL_INTEGRATION = `
  mutation DeleteEmailIntegration($id: ID!) {
    deleteEmailIntegration(id: $id)
  }
`;

// GraphQL mutation for user profile updates
const UPDATE_USER_PROFILE = `
  mutation UpdateUserProfile($userId: ID!, $input: UpdateUserProfileInput!) {
    updateUserProfile(userId: $userId, input: $input) {
      id
      email
      displayName
      firstName
      lastName
      phone
      avatarUrl
      timezone
    }
  }
`;

// GraphQL queries and mutations for organization
const GET_MY_ORGANIZATION = `
  query GetMyOrganization {
    myOrganization {
      id
      name
      vatNumber
      fiscalCode
      address
      city
      postalCode
      country
      email
      phone
      pec
      sdiCode
    }
  }
`;

const UPDATE_ORGANIZATION = `
  mutation UpdateOrganization($input: UpdateOrganizationInput!) {
    updateOrganization(input: $input) {
      success
      organization {
        id
        name
        vatNumber
        fiscalCode
        address
        city
        postalCode
        country
        email
        phone
        pec
        sdiCode
      }
      error
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

type SettingsTab = 'personal' | 'security' | 'company' | 'integrations' | 'subscription' | 'imports' | 'support' | 'accounts';

type UserRole = 'owner' | 'admin' | 'team_member' | 'collaborator';

type Permission = 'view' | 'comment' | 'edit';

interface TeamMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  avatar?: string;
  role: UserRole;
  permissions?: Permission[];
  addedAt: string;
}

// Helper to get full name
const getFullName = (member: TeamMember) => `${member.firstName} ${member.lastName}`.trim();

// =============================================================================
// MAIN COMPONENT
// =============================================================================

interface SettingsPageProps {
  activeTab?: SettingsTab;
  onTabChange?: (tab: SettingsTab) => void;
}

// Default user object when not logged in
const DEFAULT_USER = {
  id: '',
  email: '',
  displayName: '',
  firstName: '',
  lastName: '',
  avatarUrl: '',
  phone: '',
  timezone: '',
  twoFactorEnabled: false,
};

// Default settings when not available
const DEFAULT_SETTINGS = {
  theme: 'light',
  language: 'en',
  emailNotifications: true,
  pushNotifications: true,
};

export function SettingsPage({ activeTab: externalTab, onTabChange }: SettingsPageProps = {}) {
  const params = useParams<{ section?: string }>();
  const { t } = useTranslation();
  const { user: authUser, refreshUser, logout } = useAuth();
  // Get section from route params (e.g., /settings/integrations)
  const sectionFromUrl = params.section as SettingsTab | undefined;
  // Use external tab if provided, otherwise fall back to URL or default
  const [internalTab, setInternalTab] = useState<SettingsTab>(sectionFromUrl || 'personal');
  const activeTab = externalTab ?? internalTab;

  // Build user object from authUser
  const buildUserFromAuth = () => authUser ? {
    id: authUser.id,
    email: authUser.email,
    displayName: authUser.displayName,
    firstName: authUser.firstName || '',
    lastName: authUser.lastName || '',
    avatarUrl: authUser.avatarUrl || '',
    phone: authUser.phone || '',
    timezone: (authUser as any).timezone || '',
    twoFactorEnabled: false,
  } : DEFAULT_USER;

  const [user, setUser] = useState(buildUserFromAuth);
  const [settings, setSettings] = useState(() => getCurrentUserSettings() ?? DEFAULT_SETTINGS);

  // Support section refresh key - incremented when a new request is submitted
  const [supportRefreshKey, setSupportRefreshKey] = useState(0);

  // Company data state
  const DEFAULT_COMPANY = {
    name: '',
    vatNumber: '',
    fiscalCode: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'IT',
    email: '',
    phone: '',
    pec: '',
    sdiCode: '',
  };
  const [companyData, setCompanyData] = useState(DEFAULT_COMPANY);

  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Track the "saved" state to compare against for changes
  const savedUserRef = useRef(buildUserFromAuth());
  const savedSettingsRef = useRef(getCurrentUserSettings() ?? DEFAULT_SETTINGS);
  const savedCompanyRef = useRef(DEFAULT_COMPANY);

  // Check if user is admin or owner
  const isAdmin = authUser?.role === 'admin' || authUser?.role === 'owner';
  const isOwner = authUser?.role === 'owner';

  // Sync tab with URL changes (only when not controlled externally)
  useEffect(() => {
    if (!externalTab && sectionFromUrl && ['personal', 'security', 'company', 'integrations', 'subscription', 'imports', 'support', 'accounts'].includes(sectionFromUrl)) {
      setInternalTab(sectionFromUrl);
    }
  }, [sectionFromUrl, externalTab]);

  // Sync user state when authUser changes (e.g., after login/refresh)
  // Note: We preserve local avatarUrl if authUser doesn't have one (upload in progress)
  useEffect(() => {
    if (authUser) {
      const updatedUser = {
        id: authUser.id,
        email: authUser.email,
        displayName: authUser.displayName,
        firstName: authUser.firstName || '',
        lastName: authUser.lastName || '',
        avatarUrl: authUser.avatarUrl || '',
        phone: authUser.phone || '',
        timezone: (authUser as any).timezone || '',
        twoFactorEnabled: false,
      };
      setUser(updatedUser);
      // Also update the saved reference to avoid false "unsaved changes"
      savedUserRef.current = updatedUser;
    }
  }, [authUser]);

  // Load organization data on mount (for company info)
  useEffect(() => {
    async function loadOrganization() {
      try {
        const result = await graphqlQuery(GET_MY_ORGANIZATION, {});
        if (result?.myOrganization) {
          const org = result.myOrganization;
          const orgCompanyData = {
            name: org.name || '',
            vatNumber: org.vatNumber || '',
            fiscalCode: org.fiscalCode || '',
            address: org.address || '',
            city: org.city || '',
            postalCode: org.postalCode || '',
            country: org.country || 'IT',
            email: org.email || '',
            phone: org.phone || '',
            pec: org.pec || '',
            sdiCode: org.sdiCode || '',
          };
          setCompanyData(orgCompanyData);
          savedCompanyRef.current = orgCompanyData;
        }
      } catch (error) {
        console.error('[SettingsPage] Failed to load organization:', error);
      }
    }
    if (authUser?.id) {
      loadOrganization();
    }
  }, [authUser?.id]);

  // Track if there are unsaved changes (compare against saved state, not authUser)
  const hasChanges = JSON.stringify(user) !== JSON.stringify(savedUserRef.current) ||
                     JSON.stringify(settings) !== JSON.stringify(savedSettingsRef.current) ||
                     JSON.stringify(companyData) !== JSON.stringify(savedCompanyRef.current);

  // Handle avatar upload - uploads file to server, creates DB record, links to user
  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // First show preview locally
      const reader = new FileReader();
      reader.onload = (event) => {
        setUser({ ...user, avatarUrl: event.target?.result as string });
      };
      reader.readAsDataURL(file);

      // Upload to server with purpose
      try {
        const uploaded = await uploadImage(file, 'user_avatar');
        setUser(prev => ({ ...prev, avatarUrl: uploaded.url }));

        // Link FileRecord to user via setUserAvatar mutation
        if (uploaded.fileRecordId && user.id) {
          const SET_USER_AVATAR = `
            mutation SetUserAvatar($userId: ID!, $fileRecordId: ID!) {
              setUserAvatar(userId: $userId, fileRecordId: $fileRecordId)
            }
          `;
          await graphqlMutation(SET_USER_AVATAR, {
            userId: user.id,
            fileRecordId: uploaded.fileRecordId,
          });
          console.log('[SettingsPage] Avatar linked to user successfully');
          // Note: Avatar will persist on next page load via the 'me' query
        }
      } catch (error) {
        console.error('[SettingsPage] Avatar upload failed:', error);
      }
    }
  };

  // Handle avatar removal - removes file, DB record, and HasFile edge
  const handleAvatarRemove = async () => {
    if (!user.id) return;
    try {
      const REMOVE_USER_AVATAR = `
        mutation RemoveUserAvatar($userId: ID!) {
          removeUserAvatar(userId: $userId)
        }
      `;
      await graphqlMutation(REMOVE_USER_AVATAR, { userId: user.id });
      setUser(prev => ({ ...prev, avatarUrl: '' }));
      console.log('[SettingsPage] Avatar removed successfully');
    } catch (error) {
      console.error('[SettingsPage] Avatar removal failed:', error);
    }
  };

  // Handle save - persists profile changes to the database
  const handleSave = async () => {
    console.log('[SettingsPage] handleSave called, user.id:', user.id);
    if (!user.id) {
      console.error('[SettingsPage] No user.id, cannot save');
      return;
    }
    setIsSaving(true);
    const input = {
      firstName: user.firstName || null,
      lastName: user.lastName || null,
      displayName: user.displayName || null,
      phone: user.phone || null,
      timezone: user.timezone || null,
    };
    console.log('[SettingsPage] Saving with input:', input);
    let profileSaved = false;
    let orgSaved = false;
    const hasUserChanges = JSON.stringify(user) !== JSON.stringify(savedUserRef.current);
    const hasCompanyChanges = JSON.stringify(companyData) !== JSON.stringify(savedCompanyRef.current);

    try {
      // Save user profile if changed
      if (hasUserChanges) {
        const result = await graphqlMutation(UPDATE_USER_PROFILE, {
          userId: user.id,
          input,
        });
        console.log('[SettingsPage] Profile saved successfully, result:', result);
        profileSaved = true;
        savedUserRef.current = { ...user };
      }

      // Save company/organization data if changed (separate try-catch to not affect user save)
      if (hasCompanyChanges) {
        try {
          const orgInput = {
            name: companyData.name || null,
            vatNumber: companyData.vatNumber || null,
            fiscalCode: companyData.fiscalCode || null,
            address: companyData.address || null,
            city: companyData.city || null,
            postalCode: companyData.postalCode || null,
            country: companyData.country || null,
            email: companyData.email || null,
            phone: companyData.phone || null,
            pec: companyData.pec || null,
            sdiCode: companyData.sdiCode || null,
          };
          const orgResult = await graphqlMutation(UPDATE_ORGANIZATION, { input: orgInput });
          console.log('[SettingsPage] Organization save response:', JSON.stringify(orgResult, null, 2));
          if (orgResult?.updateOrganization?.success) {
            console.log('[SettingsPage] Organization saved successfully');
            orgSaved = true;
            savedCompanyRef.current = { ...companyData };
          } else {
            const errorMsg = orgResult?.updateOrganization?.error || 'Unknown error (check console for full response)';
            console.error('[SettingsPage] Organization save failed:', errorMsg);
            // Don't treat org save failure as a complete failure - user profile was saved
          }
        } catch (orgError) {
          console.error('[SettingsPage] Organization save error:', orgError);
          // Organization save failed but user profile may have been saved successfully
        }
      }

      // Update settings ref (these are saved locally)
      savedSettingsRef.current = { ...settings };

      // Only refresh user if profile was saved (not for org-only changes)
      if (profileSaved && refreshUser) {
        try {
          await refreshUser();
        } catch (refreshError) {
          console.error('[SettingsPage] User refresh failed:', refreshError);
          // Don't fail the save operation just because refresh failed
        }
      }

      // Show success if at least something was saved
      if (profileSaved || orgSaved || !hasUserChanges) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch (error) {
      console.error('[SettingsPage] Save failed:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="settings-page">
      <div className="settings-content">
        {activeTab === 'personal' && (
          <PersonalSection
            user={user}
            setUser={setUser}
            settings={settings}
            setSettings={setSettings}
            fileInputRef={fileInputRef}
            handleAvatarUpload={handleAvatarUpload}
            handleAvatarRemove={handleAvatarRemove}
            onLogout={logout}
          />
        )}
        {activeTab === 'security' && <SecuritySection />}
        {activeTab === 'company' && isOwner && (
          <CompanySection
            companyData={companyData}
            setCompanyData={setCompanyData}
          />
        )}
        {activeTab === 'integrations' && isOwner && <IntegrationsSection />}
        {activeTab === 'subscription' && isOwner && <SubscriptionSection />}
        {activeTab === 'imports' && isOwner && <ImportsSection />}
        {activeTab === 'support' && (
          <>
            <ReportIssueSection onSuccess={() => setSupportRefreshKey(k => k + 1)} />
            <MyTicketsSection refreshKey={supportRefreshKey} />
            {isAdmin && <SupportSettings key={supportRefreshKey} />}
          </>
        )}
        {activeTab === 'accounts' && isAdmin && <AccountsManagement />}
      </div>

      {/* Floating Save Button */}
      {(hasChanges || isSaving || saveSuccess) && (
        <div className="settings-save-bar">
          <span className="settings-save-hint">
            {saveSuccess ? t('settings.changesSaved') : t('settings.unsavedChanges')}
          </span>
          <button className="btn-primary" onClick={handleSave} disabled={isSaving || saveSuccess}>
            {isSaving ? (
              <>
                <RefreshCw size={16} className="spinner" />
                {t('settings.saving')}
              </>
            ) : saveSuccess ? (
              <>
                <CheckCircle2 size={16} />
                {t('settings.saved')}
              </>
            ) : (
              <>
                <Save size={16} />
                {t('settings.saveChanges')}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// PERSONAL SECTION (Profile + Preferences only)
// =============================================================================

function PersonalSection({ user, setUser, settings, setSettings, fileInputRef, handleAvatarUpload, handleAvatarRemove, onLogout }: any) {
  const { t, language, setLanguage } = useTranslation();

  return (
    <div className="settings-section">
      {/* Profile */}
      <div className="settings-panel">
        <div className="settings-panel-header">
          <User size={18} />
          <span>{t('settings.sections.profile')}</span>
        </div>
        <div className="settings-panel-content">
          {/* Avatar */}
          <div className="settings-field">
            <label className="settings-label">{t('settings.profilePhoto')}</label>
            <div className="avatar-upload">
              <div className="avatar-preview">
                {user.avatarUrl ? (
                  <img src={getPublicFileUrl(user.avatarUrl)} alt={user.displayName} />
                ) : (
                  <div className="avatar-placeholder">
                    <User size={40} />
                  </div>
                )}
                <button className="avatar-upload-btn" onClick={() => fileInputRef.current?.click()}>
                  <Camera size={16} />
                </button>
              </div>
              <div className="avatar-upload-info">
                <p className="settings-hint">{t('settings.photoHint')}</p>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button className="btn-secondary btn-sm" onClick={() => fileInputRef.current?.click()}>
                    <Upload size={14} />
                    {t('settings.upload')}
                  </button>
                  {user.avatarUrl && (
                    <button className="btn-secondary btn-sm" onClick={handleAvatarRemove} style={{ color: 'var(--color-error, #dc3545)' }}>
                      <Trash2 size={14} />
                      {t('common.remove')}
                    </button>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarUpload}
                  style={{ display: 'none' }}
                />
              </div>
            </div>
          </div>

          {/* Name */}
          <div className="settings-row">
            <div className="settings-field">
              <label className="settings-label">{t('settings.firstName')}</label>
              <input
                type="text"
                className="settings-input"
                value={user.firstName || ''}
                onChange={(e) => setUser({ ...user, firstName: e.target.value })}
                placeholder="John"
              />
            </div>
            <div className="settings-field">
              <label className="settings-label">{t('settings.lastName')}</label>
              <input
                type="text"
                className="settings-input"
                value={user.lastName || ''}
                onChange={(e) => setUser({ ...user, lastName: e.target.value })}
                placeholder="Doe"
              />
            </div>
          </div>

          {/* Contact */}
          <div className="settings-field">
            <label className="settings-label">
              <Mail size={14} />
              {t('common.email')}
            </label>
            <input
              type="email"
              className="settings-input"
              value={user.email}
              disabled
              style={{ opacity: 0.7, cursor: 'not-allowed' }}
            />
            <p className="settings-hint">{t('settings.emailChangeHint', 'Email cannot be changed')}</p>
          </div>

          <div className="settings-field">
            <label className="settings-label">
              <Phone size={14} />
              {t('settings.phone')}
            </label>
            <input
              type="tel"
              className="settings-input"
              value={user.phone || ''}
              onChange={(e) => setUser({ ...user, phone: e.target.value })}
              placeholder="+1 555 123 4567"
            />
          </div>

          <div className="settings-field">
            <label className="settings-label">
              <Globe size={14} />
              {t('settings.timezone', 'Timezone')}
            </label>
            <select
              className="settings-select"
              value={user.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone}
              onChange={(e) => setUser({ ...user, timezone: e.target.value })}
            >
              <optgroup label="Europe">
                <option value="Europe/Rome">Europe/Rome (CET/CEST)</option>
                <option value="Europe/London">Europe/London (GMT/BST)</option>
                <option value="Europe/Paris">Europe/Paris (CET/CEST)</option>
                <option value="Europe/Berlin">Europe/Berlin (CET/CEST)</option>
                <option value="Europe/Madrid">Europe/Madrid (CET/CEST)</option>
                <option value="Europe/Amsterdam">Europe/Amsterdam (CET/CEST)</option>
                <option value="Europe/Brussels">Europe/Brussels (CET/CEST)</option>
                <option value="Europe/Zurich">Europe/Zurich (CET/CEST)</option>
                <option value="Europe/Vienna">Europe/Vienna (CET/CEST)</option>
                <option value="Europe/Warsaw">Europe/Warsaw (CET/CEST)</option>
                <option value="Europe/Prague">Europe/Prague (CET/CEST)</option>
                <option value="Europe/Stockholm">Europe/Stockholm (CET/CEST)</option>
                <option value="Europe/Athens">Europe/Athens (EET/EEST)</option>
                <option value="Europe/Helsinki">Europe/Helsinki (EET/EEST)</option>
                <option value="Europe/Moscow">Europe/Moscow (MSK)</option>
              </optgroup>
              <optgroup label="Americas">
                <option value="America/New_York">America/New_York (EST/EDT)</option>
                <option value="America/Chicago">America/Chicago (CST/CDT)</option>
                <option value="America/Denver">America/Denver (MST/MDT)</option>
                <option value="America/Los_Angeles">America/Los_Angeles (PST/PDT)</option>
                <option value="America/Toronto">America/Toronto (EST/EDT)</option>
                <option value="America/Vancouver">America/Vancouver (PST/PDT)</option>
                <option value="America/Mexico_City">America/Mexico_City (CST/CDT)</option>
                <option value="America/Sao_Paulo">America/Sao_Paulo (BRT)</option>
                <option value="America/Buenos_Aires">America/Buenos_Aires (ART)</option>
              </optgroup>
              <optgroup label="Asia & Pacific">
                <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
                <option value="Asia/Shanghai">Asia/Shanghai (CST)</option>
                <option value="Asia/Hong_Kong">Asia/Hong_Kong (HKT)</option>
                <option value="Asia/Singapore">Asia/Singapore (SGT)</option>
                <option value="Asia/Seoul">Asia/Seoul (KST)</option>
                <option value="Asia/Dubai">Asia/Dubai (GST)</option>
                <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                <option value="Asia/Bangkok">Asia/Bangkok (ICT)</option>
                <option value="Australia/Sydney">Australia/Sydney (AEST/AEDT)</option>
                <option value="Australia/Melbourne">Australia/Melbourne (AEST/AEDT)</option>
                <option value="Pacific/Auckland">Pacific/Auckland (NZST/NZDT)</option>
              </optgroup>
              <optgroup label="Other">
                <option value="UTC">UTC</option>
              </optgroup>
            </select>
            <p className="settings-hint">{t('settings.timezoneHint', 'Events will be displayed in this timezone')}</p>
          </div>
        </div>
      </div>

      {/* Preferences */}
      <div className="settings-panel">
        <div className="settings-panel-header">
          <SettingsIcon size={18} />
          <span>{t('settings.sections.preferences')}</span>
        </div>
        <div className="settings-panel-content">
          <div className="settings-row">
            <div className="settings-field">
              <label className="settings-label">
                <Palette size={14} />
                {t('settings.theme')}
              </label>
              <select
                className="settings-select"
                value={settings.theme}
                onChange={(e) => setSettings({ ...settings, theme: e.target.value })}
              >
                <option value="light">{t('settings.themeLight')}</option>
                <option value="dark">{t('settings.themeDark')}</option>
                <option value="system">{t('settings.themeSystem')}</option>
              </select>
            </div>
            <div className="settings-field">
              <label className="settings-label">
                <Globe size={14} />
                {t('settings.language.title')}
              </label>
              <select
                className="settings-select"
                value={language}
                onChange={(e) => {
                  const newLang = e.target.value as 'en' | 'it';
                  setLanguage(newLang);
                  setSettings({ ...settings, language: newLang });
                  localStorage.setItem('i18nextLng', newLang);
                }}
              >
                <option value="en">{t('settings.language.english')}</option>
                <option value="it">{t('settings.language.italian')}</option>
              </select>
            </div>
          </div>

          <div className="settings-field">
            <label className="settings-label">
              <Bell size={14} />
              {t('settings.notifications')}
            </label>
            <div className="settings-toggle-row">
              <span>Email</span>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.emailNotifications}
                  onChange={(e) => setSettings({ ...settings, emailNotifications: e.target.checked })}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
            <div className="settings-toggle-row">
              <span>Push</span>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.pushNotifications}
                  onChange={(e) => setSettings({ ...settings, pushNotifications: e.target.checked })}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Logout */}
      <div className="settings-panel">
        <div className="settings-panel-header">
          <LogOut size={18} />
          <span>{t('settings.logout', 'Logout')}</span>
        </div>
        <div className="settings-panel-content">
          <p className="settings-hint" style={{ marginBottom: '16px' }}>
            {t('settings.logoutHint', 'Sign out from your account on this device.')}
          </p>
          <button
            className="btn-secondary"
            onClick={onLogout}
            style={{
              color: 'var(--color-error, #dc3545)',
              borderColor: 'var(--color-error, #dc3545)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <LogOut size={16} />
            {t('settings.logoutButton', 'Disconnect')}
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// SECURITY SECTION (Password + 2FA)
// =============================================================================

// GraphQL mutations for 2FA
const SETUP_TWO_FACTOR = `
  mutation SetupTwoFactor($userId: ID!) {
    setupTwoFactor(userId: $userId) {
      success
      secret
      qrCodeUrl
      backupCodes
      error
    }
  }
`;

const VERIFY_TWO_FACTOR = `
  mutation VerifyTwoFactor($userId: ID!, $input: TwoFactorVerifyInput!) {
    verifyTwoFactor(userId: $userId, input: $input) {
      success
      error
    }
  }
`;

const DISABLE_TWO_FACTOR = `
  mutation DisableTwoFactor($userId: ID!, $input: TwoFactorVerifyInput!) {
    disableTwoFactor(userId: $userId, input: $input) {
      success
      error
    }
  }
`;

type TwoFactorStep = 'idle' | 'setup' | 'verify' | 'enabled' | 'disable';

function SecuritySection() {
  const { t } = useTranslation();
  const { user: authUser } = useAuth();

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // 2FA state
  const [twoFactorStep, setTwoFactorStep] = useState<TwoFactorStep>('idle');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [verificationCode, setVerificationCode] = useState('');
  const [twoFactorError, setTwoFactorError] = useState('');
  const [isLoading2FA, setIsLoading2FA] = useState(false);

  const handleChangePassword = async () => {
    setPasswordError('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError(t('settings.fillAllFields', 'Please fill all fields'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError(t('auth.passwordsMismatch', 'Passwords do not match'));
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError(t('auth.passwordTooShort', 'Password must be at least 8 characters'));
      return;
    }

    setIsChangingPassword(true);
    try {
      // TODO: Implement password change mutation
      await new Promise(resolve => setTimeout(resolve, 1000));
      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordSuccess(false), 3000);
    } catch (error) {
      setPasswordError(t('settings.passwordChangeFailed', 'Failed to change password'));
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Start 2FA setup - get QR code
  const handleSetup2FA = async () => {
    if (!authUser?.id) return;

    setIsLoading2FA(true);
    setTwoFactorError('');

    try {
      const result = await graphqlMutation(SETUP_TWO_FACTOR, { userId: authUser.id });
      if (result.setupTwoFactor?.success) {
        setQrCodeUrl(result.setupTwoFactor.qrCodeUrl);
        setBackupCodes(result.setupTwoFactor.backupCodes || []);
        setTwoFactorStep('setup');
      } else {
        setTwoFactorError(result.setupTwoFactor?.error || 'Failed to setup 2FA');
      }
    } catch (error) {
      setTwoFactorError('Failed to setup 2FA');
    } finally {
      setIsLoading2FA(false);
    }
  };

  // Verify 2FA code and enable
  const handleVerify2FA = async () => {
    if (!authUser?.id || !verificationCode) return;

    setIsLoading2FA(true);
    setTwoFactorError('');

    try {
      const result = await graphqlMutation(VERIFY_TWO_FACTOR, {
        userId: authUser.id,
        input: { code: verificationCode }
      });
      if (result.verifyTwoFactor?.success) {
        setTwoFactorStep('enabled');
        setVerificationCode('');
      } else {
        setTwoFactorError(result.verifyTwoFactor?.error || 'Invalid verification code');
      }
    } catch (error) {
      setTwoFactorError('Failed to verify code');
    } finally {
      setIsLoading2FA(false);
    }
  };

  // Disable 2FA
  const handleDisable2FA = async () => {
    if (!authUser?.id || !verificationCode) return;

    setIsLoading2FA(true);
    setTwoFactorError('');

    try {
      const result = await graphqlMutation(DISABLE_TWO_FACTOR, {
        userId: authUser.id,
        input: { code: verificationCode }
      });
      if (result.disableTwoFactor?.success) {
        setTwoFactorStep('idle');
        setVerificationCode('');
        setQrCodeUrl('');
        setBackupCodes([]);
      } else {
        setTwoFactorError(result.disableTwoFactor?.error || 'Invalid verification code');
      }
    } catch (error) {
      setTwoFactorError('Failed to disable 2FA');
    } finally {
      setIsLoading2FA(false);
    }
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
  };

  return (
    <div className="settings-section">
      {/* Change Password */}
      <div className="settings-panel">
        <div className="settings-panel-header">
          <Key size={18} />
          <span>{t('settings.changePassword')}</span>
        </div>
        <div className="settings-panel-content">
          {passwordError && (
            <div className="settings-error" style={{ marginBottom: '1rem', padding: '0.75rem', background: 'var(--color-error-bg, #fef2f2)', color: 'var(--color-error, #dc3545)', borderRadius: '0.5rem' }}>
              {passwordError}
            </div>
          )}
          {passwordSuccess && (
            <div className="settings-success" style={{ marginBottom: '1rem', padding: '0.75rem', background: 'var(--color-success-bg, #f0fdf4)', color: 'var(--color-success, #22c55e)', borderRadius: '0.5rem' }}>
              {t('settings.passwordChanged', 'Password changed successfully')}
            </div>
          )}

          <div className="settings-field">
            <label className="settings-label">{t('settings.currentPassword', 'Current Password')}</label>
            <input
              type="password"
              className="settings-input"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="••••••••"
            />
          </div>

          <div className="settings-field">
            <label className="settings-label">{t('settings.newPassword')}</label>
            <input
              type="password"
              className="settings-input"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="••••••••"
            />
          </div>

          <div className="settings-field">
            <label className="settings-label">{t('settings.confirmPassword')}</label>
            <input
              type="password"
              className="settings-input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
            />
          </div>

          <button
            className="btn-primary"
            onClick={handleChangePassword}
            disabled={isChangingPassword}
            style={{ marginTop: '1rem' }}
          >
            {isChangingPassword ? t('common.saving', 'Saving...') : t('settings.updatePassword')}
          </button>
        </div>
      </div>

      {/* Two-Factor Authentication */}
      <div className="settings-panel">
        <div className="settings-panel-header">
          <Shield size={18} />
          <span>{t('settings.twoFactorAuth')}</span>
        </div>
        <div className="settings-panel-content">
          {twoFactorError && (
            <div className="settings-error" style={{ marginBottom: '1rem', padding: '0.75rem', background: 'var(--color-error-bg, #fef2f2)', color: 'var(--color-error, #dc3545)', borderRadius: '0.5rem' }}>
              {twoFactorError}
            </div>
          )}

          {/* Idle state - not enabled */}
          {twoFactorStep === 'idle' && (
            <div className="settings-field">
              <p className="settings-hint" style={{ marginBottom: '1rem' }}>
                {t('settings.2faDescription', 'Add an extra layer of security to your account by requiring a verification code from your authenticator app.')}
              </p>
              <button
                className="btn-primary"
                onClick={handleSetup2FA}
                disabled={isLoading2FA}
              >
                <Smartphone size={16} />
                {isLoading2FA ? t('common.loading', 'Loading...') : t('settings.setup2FA', 'Setup Two-Factor Authentication')}
              </button>
            </div>
          )}

          {/* Setup state - show QR code */}
          {twoFactorStep === 'setup' && (
            <div className="two-factor-setup">
              <div className="setup-step">
                <h4>1. {t('settings.2fa.scanQR', 'Scan QR Code')}</h4>
                <p className="settings-hint">
                  {t('settings.2fa.scanDescription', 'Open your authenticator app (Google Authenticator, Authy, etc.) and scan this QR code.')}
                </p>
                <div className="qr-code-container" style={{ margin: '1rem 0', padding: '1rem', background: 'white', borderRadius: '8px', display: 'inline-block' }}>
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrCodeUrl)}`}
                    alt="2FA QR Code"
                    style={{ display: 'block' }}
                  />
                </div>
              </div>

              <div className="setup-step" style={{ marginTop: '1.5rem' }}>
                <h4>2. {t('settings.2fa.enterCode', 'Enter Verification Code')}</h4>
                <p className="settings-hint">
                  {t('settings.2fa.enterDescription', 'Enter the 6-digit code from your authenticator app to verify setup.')}
                </p>
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <input
                    type="text"
                    className="settings-input"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    style={{ width: '120px', textAlign: 'center', letterSpacing: '4px', fontSize: '1.25rem' }}
                  />
                  <button
                    className="btn-primary"
                    onClick={handleVerify2FA}
                    disabled={isLoading2FA || verificationCode.length !== 6}
                  >
                    {isLoading2FA ? t('common.verifying', 'Verifying...') : t('common.verify', 'Verify')}
                  </button>
                </div>
              </div>

              <button
                className="btn-secondary"
                onClick={() => {
                  setTwoFactorStep('idle');
                  setQrCodeUrl('');
                  setBackupCodes([]);
                  setVerificationCode('');
                }}
                style={{ marginTop: '1rem' }}
              >
                {t('common.cancel', 'Cancel')}
              </button>
            </div>
          )}

          {/* Enabled state - show backup codes */}
          {twoFactorStep === 'enabled' && (
            <div className="two-factor-enabled">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', color: 'var(--color-success, #22c55e)' }}>
                <CheckCircle2 size={20} />
                <span style={{ fontWeight: 600 }}>{t('settings.2fa.enabled', 'Two-Factor Authentication is enabled')}</span>
              </div>

              {backupCodes.length > 0 && (
                <div className="backup-codes" style={{ marginBottom: '1.5rem' }}>
                  <h4>{t('settings.2fa.backupCodes', 'Backup Codes')}</h4>
                  <p className="settings-hint">
                    {t('settings.2fa.backupDescription', 'Save these codes in a safe place. You can use them to access your account if you lose your authenticator device.')}
                  </p>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '0.5rem',
                    marginTop: '0.75rem',
                    padding: '1rem',
                    background: 'var(--surface-secondary, #f9fafb)',
                    borderRadius: '8px',
                    fontFamily: 'monospace'
                  }}>
                    {backupCodes.map((code, i) => (
                      <div key={i} style={{ padding: '0.25rem 0.5rem' }}>{code}</div>
                    ))}
                  </div>
                  <button
                    className="btn-secondary btn-sm"
                    onClick={copyBackupCodes}
                    style={{ marginTop: '0.75rem' }}
                  >
                    {t('common.copy', 'Copy Codes')}
                  </button>
                </div>
              )}

              <button
                className="btn-primary"
                onClick={() => setTwoFactorStep('idle')}
              >
                {t('common.done', 'Done')}
              </button>
            </div>
          )}

          {/* Disable confirmation */}
          {twoFactorStep === 'disable' && (
            <div className="two-factor-disable">
              <p className="settings-hint" style={{ marginBottom: '1rem' }}>
                {t('settings.2fa.disableDescription', 'Enter your authenticator code or a backup code to disable two-factor authentication.')}
              </p>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="text"
                  className="settings-input"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  placeholder="Enter code"
                  style={{ width: '150px' }}
                />
                <button
                  className="btn-primary"
                  onClick={handleDisable2FA}
                  disabled={isLoading2FA || !verificationCode}
                  style={{ background: 'var(--color-error, #dc3545)' }}
                >
                  {isLoading2FA ? t('common.loading', 'Loading...') : t('settings.2fa.disable', 'Disable 2FA')}
                </button>
              </div>
              <button
                className="btn-secondary"
                onClick={() => {
                  setTwoFactorStep('idle');
                  setVerificationCode('');
                }}
                style={{ marginTop: '0.75rem' }}
              >
                {t('common.cancel', 'Cancel')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// COMPANY SECTION (Business Data)
// =============================================================================

interface CompanySectionProps {
  companyData: {
    name: string;
    vatNumber: string;
    fiscalCode: string;
    address: string;
    city: string;
    postalCode: string;
    country: string;
    email: string;
    phone: string;
    pec: string;
    sdiCode: string;
  };
  setCompanyData: React.Dispatch<React.SetStateAction<CompanySectionProps['companyData']>>;
}

function CompanySection({ companyData, setCompanyData }: CompanySectionProps) {
  const { t } = useTranslation();

  return (
    <div className="settings-section">
      <div className="settings-panel">
        <div className="settings-panel-header">
          <Building2 size={18} />
          <span>{t('settings.companyData')}</span>
        </div>
        <div className="settings-panel-content">
          <p className="settings-hint" style={{ marginBottom: '1rem' }}>
            {t('settings.companyDataDescription')}
          </p>

          {/* Company Name */}
          <div className="settings-field">
            <label className="settings-label">{t('settings.companyName')}</label>
            <input
              type="text"
              className="settings-input"
              value={companyData.name}
              onChange={(e) => setCompanyData({ ...companyData, name: e.target.value })}
              placeholder={t('settings.companyNamePlaceholder')}
            />
          </div>

          {/* VAT and Fiscal Code */}
          <div className="settings-row">
            <div className="settings-field">
              <label className="settings-label">{t('settings.vatNumber')}</label>
              <input
                type="text"
                className="settings-input"
                value={companyData.vatNumber}
                onChange={(e) => setCompanyData({ ...companyData, vatNumber: e.target.value })}
                placeholder="IT12345678901"
              />
            </div>
            <div className="settings-field">
              <label className="settings-label">{t('settings.fiscalCode')}</label>
              <input
                type="text"
                className="settings-input"
                value={companyData.fiscalCode}
                onChange={(e) => setCompanyData({ ...companyData, fiscalCode: e.target.value })}
                placeholder="12345678901"
              />
            </div>
          </div>

          {/* Address */}
          <div className="settings-field">
            <label className="settings-label">{t('settings.address')}</label>
            <input
              type="text"
              className="settings-input"
              value={companyData.address}
              onChange={(e) => setCompanyData({ ...companyData, address: e.target.value })}
              placeholder={t('settings.addressPlaceholder')}
            />
          </div>

          {/* City, Postal Code, Country */}
          <div className="settings-row">
            <div className="settings-field" style={{ flex: 2 }}>
              <label className="settings-label">{t('settings.city')}</label>
              <input
                type="text"
                className="settings-input"
                value={companyData.city}
                onChange={(e) => setCompanyData({ ...companyData, city: e.target.value })}
              />
            </div>
            <div className="settings-field" style={{ flex: 1 }}>
              <label className="settings-label">{t('settings.postalCode')}</label>
              <input
                type="text"
                className="settings-input"
                value={companyData.postalCode}
                onChange={(e) => setCompanyData({ ...companyData, postalCode: e.target.value })}
              />
            </div>
            <div className="settings-field" style={{ flex: 1 }}>
              <label className="settings-label">{t('settings.country')}</label>
              <select
                className="settings-select"
                value={companyData.country}
                onChange={(e) => setCompanyData({ ...companyData, country: e.target.value })}
              >
                <optgroup label="EU Countries">
                  <option value="AT">Austria</option>
                  <option value="BE">Belgium</option>
                  <option value="BG">Bulgaria</option>
                  <option value="HR">Croatia</option>
                  <option value="CY">Cyprus</option>
                  <option value="CZ">Czech Republic</option>
                  <option value="DK">Denmark</option>
                  <option value="EE">Estonia</option>
                  <option value="FI">Finland</option>
                  <option value="FR">France</option>
                  <option value="DE">Germany</option>
                  <option value="GR">Greece</option>
                  <option value="HU">Hungary</option>
                  <option value="IE">Ireland</option>
                  <option value="IT">Italia</option>
                  <option value="LV">Latvia</option>
                  <option value="LT">Lithuania</option>
                  <option value="LU">Luxembourg</option>
                  <option value="MT">Malta</option>
                  <option value="NL">Netherlands</option>
                  <option value="PL">Poland</option>
                  <option value="PT">Portugal</option>
                  <option value="RO">Romania</option>
                  <option value="SK">Slovakia</option>
                  <option value="SI">Slovenia</option>
                  <option value="ES">Spain</option>
                  <option value="SE">Sweden</option>
                </optgroup>
                <optgroup label="Other Countries">
                  <option value="CH">Switzerland</option>
                  <option value="UK">United Kingdom</option>
                  <option value="NO">Norway</option>
                  <option value="IS">Iceland</option>
                  <option value="LI">Liechtenstein</option>
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                  <option value="AU">Australia</option>
                </optgroup>
              </select>
            </div>
          </div>

          {/* Email and Phone */}
          <div className="settings-row">
            <div className="settings-field">
              <label className="settings-label">{t('settings.companyEmail')}</label>
              <input
                type="email"
                className="settings-input"
                value={companyData.email}
                onChange={(e) => setCompanyData({ ...companyData, email: e.target.value })}
                placeholder="info@company.com"
              />
            </div>
            <div className="settings-field">
              <label className="settings-label">{t('settings.companyPhone')}</label>
              <input
                type="tel"
                className="settings-input"
                value={companyData.phone}
                onChange={(e) => setCompanyData({ ...companyData, phone: e.target.value })}
                placeholder="+39 02 1234567"
              />
            </div>
          </div>

          {/* E-Invoicing: PEC and SDI */}
          <div className="settings-row">
            <div className="settings-field">
              <label className="settings-label">{t('settings.pec')}</label>
              <input
                type="email"
                className="settings-input"
                value={companyData.pec}
                onChange={(e) => setCompanyData({ ...companyData, pec: e.target.value })}
                placeholder="company@pec.it"
              />
              <p className="settings-hint">{t('settings.pecHint')}</p>
            </div>
            <div className="settings-field">
              <label className="settings-label">{t('settings.sdiCode')}</label>
              <input
                type="text"
                className="settings-input"
                value={companyData.sdiCode}
                onChange={(e) => setCompanyData({ ...companyData, sdiCode: e.target.value.toUpperCase() })}
                placeholder="XXXXXXX"
                maxLength={7}
              />
              <p className="settings-hint">{t('settings.sdiCodeHint')}</p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

// =============================================================================
// INTEGRATIONS SECTION
// =============================================================================

type IntegrationStatus = 'connected' | 'disconnected' | 'pending' | 'error';

interface IntegrationState {
  google: IntegrationStatus;
  microsoft: IntegrationStatus;
  email: IntegrationStatus;
  dropbox: IntegrationStatus;
  slack: IntegrationStatus;
  notion: IntegrationStatus;
}

function IntegrationsSection() {
  const { t } = useTranslation();
  const { data: googleConnection } = useGoogleConnection();
  const isGoogleConnected = googleConnection?.isConnected ?? false;

  // Feature flag: hide non-Google integrations for now
  const SHOW_OTHER_INTEGRATIONS = false;

  // Integration states
  const [integrationStatus, setIntegrationStatus] = useState<IntegrationState>({
    google: isGoogleConnected ? 'connected' : 'disconnected',
    microsoft: 'disconnected',
    email: 'disconnected',
    dropbox: 'disconnected',
    slack: 'disconnected',
    notion: 'disconnected',
  });

  // Expanded panels
  const [expandedPanel, setExpandedPanel] = useState<string | null>(null);

  // Email mode: editing | testing | connected
  const [emailMode, setEmailMode] = useState<'editing' | 'testing' | 'connected'>('editing');
  const [emailError, setEmailError] = useState<string | null>(null);

  // Email config state
  const [emailConfig, setEmailConfig] = useState({
    provider: 'custom',
    imapHost: '',
    imapPort: '993',
    smtpHost: '',
    smtpPort: '587',
    email: '',
    password: '',
    useSsl: true,
  });

  // Microsoft config state
  const [microsoftConfig, setMicrosoftConfig] = useState({
    tenantId: '',
    clientId: '',
  });

  // State for email integration ID (from DB)
  const [emailIntegrationId, setEmailIntegrationId] = useState<string | null>(null);

  // Load saved email config from GraphQL on mount
  useEffect(() => {
    loadEmailConfig();
  }, []);

  const loadEmailConfig = async () => {
    try {
      // First, check if IMAP server is already connected
      const statusRes = await fetch('/api/imap/status');
      const statusData = await statusRes.json();

      if (statusData.success && statusData.imap?.connected) {
        // Server is connected - show connected state
        setIntegrationStatus(prev => ({ ...prev, email: 'connected' }));
        setEmailMode('connected');

        // Pre-fill form with server info
        setEmailConfig(prev => ({
          ...prev,
          email: statusData.imap.user || prev.email,
          imapHost: statusData.imap.host || prev.imapHost,
          smtpHost: statusData.smtp?.host || prev.smtpHost,
        }));
      }

      // Then check database for full config
      const result = await graphqlQuery<{ emailIntegrations: Array<{
        id: string;
        provider: string;
        email: string;
        status: string;
        imapHost: string | null;
        imapPort: number | null;
        imapTls: boolean | null;
        smtpHost: string | null;
        smtpPort: number | null;
        smtpSecure: boolean | null;
      }> }>(GET_EMAIL_INTEGRATIONS, {});

      if (result.emailIntegrations?.length > 0) {
        const saved = result.emailIntegrations[0];
        setEmailIntegrationId(saved.id);
        setEmailConfig({
          provider: saved.provider || 'custom',
          email: saved.email || '',
          password: '', // Never returned from server
          imapHost: saved.imapHost || '',
          imapPort: String(saved.imapPort || 993),
          smtpHost: saved.smtpHost || '',
          smtpPort: String(saved.smtpPort || 587),
          useSsl: saved.imapTls !== false,
        });
        if (saved.status === 'connected') {
          setIntegrationStatus(prev => ({ ...prev, email: 'connected' }));
          setEmailMode('connected');
        }
      }
    } catch (e) {
      console.error('Failed to load email config:', e);
    }
  };


  // Single handler for Connect/Modify button
  const handleEmailAction = async () => {
    // If connected, switch to editing mode
    if (emailMode === 'connected') {
      setEmailMode('editing');
      return;
    }

    // Validate required fields
    if (!emailConfig.imapHost || !emailConfig.email || !emailConfig.password) {
      setEmailError(t('settings.email.fillAllFields'));
      return;
    }

    setEmailMode('testing');
    setEmailError(null);

    try {
      // 1. Test the IMAP connection
      const res = await fetch('/api/imap/connect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imap: {
            host: emailConfig.imapHost,
            port: parseInt(emailConfig.imapPort),
            user: emailConfig.email,
            password: emailConfig.password,
            tls: emailConfig.useSsl,
          },
          smtp: {
            host: emailConfig.smtpHost || emailConfig.imapHost,
            port: parseInt(emailConfig.smtpPort),
            user: emailConfig.email,
            password: emailConfig.password,
            secure: emailConfig.useSsl && parseInt(emailConfig.smtpPort) === 465,
          },
        }),
      });

      const data = await res.json();

      if (!data.success) {
        throw new Error(data.error || t('settings.email.connectionFailed'));
      }

      // 2. Save to database via GraphQL
      const result = await graphqlMutation<{ saveEmailIntegration: { id: string } }>(
        SAVE_EMAIL_INTEGRATION,
        {
          input: {
            provider: emailConfig.provider,
            email: emailConfig.email,
            status: 'connected',
            syncEnabled: true,
            connectedAt: new Date().toISOString(),
            imapHost: emailConfig.imapHost,
            imapPort: parseInt(emailConfig.imapPort),
            imapTls: emailConfig.useSsl,
            smtpHost: emailConfig.smtpHost || emailConfig.imapHost,
            smtpPort: parseInt(emailConfig.smtpPort),
            smtpSecure: emailConfig.useSsl && parseInt(emailConfig.smtpPort) === 465,
            password: emailConfig.password,
          },
        }
      );

      if (result.saveEmailIntegration?.id) {
        setEmailIntegrationId(result.saveEmailIntegration.id);
      }

      // 3. Success - switch to connected mode
      setIntegrationStatus(prev => ({ ...prev, email: 'connected' }));
      setEmailMode('connected');
      setEmailError(null);

    } catch (err: any) {
      console.error('Email connection error:', err);
      setEmailError(err.message || t('settings.email.connectionError'));
      setIntegrationStatus(prev => ({ ...prev, email: 'error' }));
      setEmailMode('editing');
    }
  };

  // Disconnect email
  const handleDisconnectEmail = async () => {
    // First, disconnect from IMAP server
    try {
      await fetch('/api/imap/disconnect', { method: 'POST' });
      console.log('[Settings] Disconnected from IMAP/SMTP server');
    } catch (e) {
      console.error('Failed to disconnect from IMAP server:', e);
    }

    // Delete from database via GraphQL if we have an ID
    if (emailIntegrationId) {
      try {
        await graphqlMutation(DELETE_EMAIL_INTEGRATION, { id: emailIntegrationId });
        console.log('[Settings] Email integration deleted from database');
      } catch (e) {
        console.error('Failed to delete email integration from database:', e);
        // Continue anyway to clear local state
      }
    }

    // Also clear localStorage for backwards compatibility
    localStorage.removeItem('ba_email_config');

    // Reset connection state but keep form data for easy reconnection
    setEmailIntegrationId(null);
    setIntegrationStatus(prev => ({ ...prev, email: 'disconnected' }));
    setEmailMode('editing');
    // Clear only the password for security, keep other fields
    setEmailConfig(prev => ({ ...prev, password: '' }));
  };

  // =========================================
  // OAUTH CONNECT HANDLERS
  // =========================================

  // Generic OAuth connect handler
  const handleOAuthConnect = async (provider: 'microsoft' | 'dropbox' | 'slack' | 'notion') => {
    setIntegrationStatus(prev => ({ ...prev, [provider]: 'pending' }));

    try {
      // Get OAuth URL from backend
      const response = await fetch(`/api/${provider}/auth/init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();

      if (!data.success || !data.authUrl) {
        console.error(`Failed to get ${provider} auth URL:`, data.error);
        setIntegrationStatus(prev => ({ ...prev, [provider]: 'error' }));
        return;
      }

      // Open popup for OAuth
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.innerWidth - width) / 2;
      const top = window.screenY + (window.innerHeight - height) / 2;
      const popup = window.open(
        data.authUrl,
        `${provider}-oauth`,
        `width=${width},height=${height},left=${left},top=${top},popup=yes`
      );

      // Listen for OAuth callback
      const messageHandler = async (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type !== `${provider}-oauth-callback`) return;

        window.removeEventListener('message', messageHandler);

        if (event.data.error) {
          console.error(`${provider} OAuth error:`, event.data.error);
          setIntegrationStatus(prev => ({ ...prev, [provider]: 'error' }));
          return;
        }

        // Exchange code for tokens
        try {
          const callbackResponse = await fetch(`/api/${provider}/auth/callback`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              code: event.data.code,
              state: event.data.state,
            }),
          });
          const callbackData = await callbackResponse.json();

          if (callbackData.success) {
            setIntegrationStatus(prev => ({ ...prev, [provider]: 'connected' }));
          } else {
            console.error(`${provider} token exchange failed:`, callbackData.error);
            setIntegrationStatus(prev => ({ ...prev, [provider]: 'error' }));
          }
        } catch (e) {
          console.error(`${provider} callback error:`, e);
          setIntegrationStatus(prev => ({ ...prev, [provider]: 'error' }));
        }
      };

      window.addEventListener('message', messageHandler);

      // Cleanup if popup is closed without completing
      const checkClosed = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', messageHandler);
          // Only reset if still pending
          setIntegrationStatus(prev =>
            prev[provider] === 'pending' ? { ...prev, [provider]: 'disconnected' } : prev
          );
        }
      }, 500);

    } catch (e) {
      console.error(`${provider} OAuth error:`, e);
      setIntegrationStatus(prev => ({ ...prev, [provider]: 'error' }));
    }
  };

  // Disconnect handler
  const handleOAuthDisconnect = async (provider: 'microsoft' | 'dropbox' | 'slack' | 'notion') => {
    try {
      await fetch(`/api/${provider}/auth/disconnect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
    } catch (e) {
      console.error(`Failed to disconnect ${provider}:`, e);
    }
    setIntegrationStatus(prev => ({ ...prev, [provider]: 'disconnected' }));
  };

  const togglePanel = (panelId: string) => {
    setExpandedPanel(expandedPanel === panelId ? null : panelId);
  };

  const getStatusBadge = (status: IntegrationStatus) => {
    switch (status) {
      case 'connected':
        return <span className="int-status int-status--connected"><CheckCircle2 size={14} /> Connected</span>;
      case 'pending':
        return <span className="int-status int-status--pending"><RefreshCw size={14} className="spinner" /> Connecting</span>;
      case 'error':
        return <span className="int-status int-status--error"><AlertCircle size={14} /> Error</span>;
      default:
        return <span className="int-status int-status--disconnected">Not connected</span>;
    }
  };

  // Render a Google service row with switch
  const renderGoogleService = (serviceId: string, label: string, icon: React.ReactNode) => {
    const service = googleConnection?.services?.[serviceId as keyof typeof googleConnection.services];
    const isAuthorized = service?.status === 'active';
    const isEnabled = isAuthorized; // For now, enabled = authorized

    return (
      <div className={`int-service-row ${!isAuthorized ? 'int-service-row--locked' : ''}`}>
        <div className="int-service-row__info">
          {icon}
          <span className="int-service-row__label">{label}</span>
        </div>
        {isAuthorized ? (
          <label className="int-switch">
            <input
              type="checkbox"
              checked={isEnabled}
              onChange={() => {
                // TODO: Toggle service enabled/disabled state
                console.log(`Toggle ${serviceId}:`, !isEnabled);
              }}
            />
            <span className="int-switch__slider"></span>
          </label>
        ) : (
          <div className="int-service-row__locked">
            <Lock size={14} />
            <span className="int-service-row__locked-text">
              {t('settings.serviceNotAuthorized')}
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="settings-section integrations-section">
      {/* Section Header */}
      <div className="int-section-header">
        <LinkIcon size={24} />
        <div>
          <h2>{t('settings.sections.integrations')}</h2>
          <p>Connect your tools to sync data and communications</p>
        </div>
      </div>

      {/* ============================================
          GOOGLE WORKSPACE
          ============================================ */}
      <div className="int-card">
        <div className="int-card__header">
          <div className="int-card__title">
            <GoogleLogo />
            <h3>Google Workspace</h3>
          </div>
          {isGoogleConnected ? (
            <span className="int-status int-status--connected"><CheckCircle2 size={14} /> Connected</span>
          ) : (
            <span className="int-status int-status--disconnected">Not connected</span>
          )}
        </div>

        <div className="int-card__content">
          {isGoogleConnected ? (
            <>
              {/* Services with Switches */}
              <div className="int-services-list">
                <h4>{t('settings.activeServices')}</h4>
                {renderGoogleService('gmail', 'Gmail', <Mail size={18} />)}
                {renderGoogleService('calendar', 'Calendar', <CalendarDays size={18} />)}
                {renderGoogleService('drive', 'Drive', <HardDrive size={18} />)}
                {renderGoogleService('docs', 'Docs', <FileText size={18} />)}
                {renderGoogleService('sheets', 'Sheets', <Table size={18} />)}
                {renderGoogleService('meet', 'Meet', <Video size={18} />)}
                {renderGoogleService('contacts', 'Contacts', <Users size={18} />)}
              </div>

              {/* Connected Account + Disconnect */}
              <div className="int-card__footer">
                <div className="int-connected-account">
                  <span>{t('settings.connectedTo')}</span>
                  <strong>{googleConnection?.googleEmail}</strong>
                </div>
                <GoogleConnectButton />
              </div>
            </>
          ) : (
            <>
              <p className="int-card__description">
                {t('settings.googleDescription')}
              </p>
              <div className="int-card__actions">
                <GoogleConnectButton />
              </div>
            </>
          )}
        </div>
      </div>

      {/* ============================================
          MICROSOFT 365
          ============================================ */}
      {SHOW_OTHER_INTEGRATIONS && <div className={`int-box ${integrationStatus.microsoft === 'connected' ? 'int-box--connected' : ''}`}>
        <div className="int-box__header" onClick={() => togglePanel('microsoft')}>
          <div className="int-box__logo">
            <MicrosoftLogo />
          </div>
          <div className="int-box__info">
            <h3>Microsoft 365</h3>
            <p>Teams, Outlook, OneDrive, SharePoint</p>
          </div>
          {getStatusBadge(integrationStatus.microsoft)}
          <ChevronDown size={20} className={`int-box__chevron ${expandedPanel === 'microsoft' ? 'expanded' : ''}`} />
        </div>

        {expandedPanel === 'microsoft' && (
          <div className="int-box__content">
            <div className="int-box__description">
              <p>
                Integrate Microsoft 365 to sync Teams, Outlook, OneDrive and SharePoint.
                Requires a Microsoft business or personal account.
              </p>
            </div>

            {integrationStatus.microsoft === 'connected' ? (
              <>
                <div className="int-services">
                  <h4>Servizi attivi</h4>
                  <div className="int-services__grid">
                    <label className="int-service">
                      <input type="checkbox" defaultChecked />
                      <TeamsIcon />
                      <span>Teams</span>
                    </label>
                    <label className="int-service">
                      <input type="checkbox" defaultChecked />
                      <Mail size={18} />
                      <span>Outlook</span>
                    </label>
                    <label className="int-service">
                      <input type="checkbox" />
                      <OneDriveIcon />
                      <span>OneDrive</span>
                    </label>
                    <label className="int-service">
                      <input type="checkbox" />
                      <SharePointIcon />
                      <span>SharePoint</span>
                    </label>
                  </div>
                </div>

                <div className="int-box__actions">
                  <button className="btn-secondary btn-sm">
                    <RefreshCw size={14} />
                    Sync
                  </button>
                  <button className="btn-danger btn-sm" onClick={() => handleOAuthDisconnect('microsoft')}>
                    Disconnect
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="int-box__instructions">
                  <h4>{t('settings.howToConnect')}</h4>
                  <ol>
                    <li>{t('settings.microsoftInstructions.step1')}</li>
                    <li>{t('settings.microsoftInstructions.step2')}</li>
                    <li>{t('settings.microsoftInstructions.step3')}</li>
                    <li>{t('settings.microsoftInstructions.step4')}</li>
                  </ol>
                </div>

                <div className="int-box__advanced">
                  <button
                    className="int-toggle-advanced"
                    onClick={(e) => {
                      e.stopPropagation();
                      const el = e.currentTarget.nextElementSibling;
                      el?.classList.toggle('visible');
                    }}
                  >
                    Advanced configuration (Azure AD)
                    <ChevronDown size={16} />
                  </button>
                  <div className="int-advanced-fields">
                    <div className="settings-field">
                      <label className="settings-label">Tenant ID</label>
                      <input
                        type="text"
                        className="settings-input"
                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                        value={microsoftConfig.tenantId}
                        onChange={(e) => setMicrosoftConfig({...microsoftConfig, tenantId: e.target.value})}
                      />
                      <p className="settings-hint">Find the Tenant ID in the Azure AD portal</p>
                    </div>
                    <div className="settings-field">
                      <label className="settings-label">Client ID</label>
                      <input
                        type="text"
                        className="settings-input"
                        placeholder="Registered application ID"
                        value={microsoftConfig.clientId}
                        onChange={(e) => setMicrosoftConfig({...microsoftConfig, clientId: e.target.value})}
                      />
                    </div>
                  </div>
                </div>

                <div className="int-box__actions">
                  <button
                    className="btn-microsoft"
                    onClick={() => handleOAuthConnect('microsoft')}
                    disabled={integrationStatus.microsoft === 'pending'}
                  >
                    {integrationStatus.microsoft === 'pending' ? (
                      <>
                        <RefreshCw size={18} className="spinner" />
                        {t('settings.email.connecting')}
                      </>
                    ) : (
                      <>
                        <MicrosoftLogo />
                        Connect with Microsoft
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>}

      {/* ============================================
          EMAIL SETTINGS
          ============================================ */}
      <div className="int-card">
        <div className="int-card__header">
          <div className="int-card__title">
            <Mail size={24} />
            <h3>{t('settings.email.emailSettings')}</h3>
          </div>
          {emailMode === 'connected' ? (
            <span className="int-status int-status--connected"><CheckCircle2 size={14} /> Connected</span>
          ) : (
            <span className="int-status int-status--disconnected">Not connected</span>
          )}
        </div>

        <div className="int-card__content">
          {emailMode === 'connected' ? (
            <>
              {/* Connected state - show only email and disconnect */}
              <div className="int-card__footer">
                <div className="int-connected-account">
                  <span>{t('settings.connectedTo')}</span>
                  <strong>{emailConfig.email}</strong>
                  <span className="int-provider-tag">{emailConfig.provider}</span>
                </div>
                <button className="google-disconnect-btn" onClick={handleDisconnectEmail}>
                  {t('settings.email.disconnect')}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Disconnected state - show provider selection and form */}

              {/* Provider Quick Select */}
              <div className="int-email-providers">
                <h4>{t('settings.email.selectProvider')}</h4>
                <div className="int-provider-grid">
                  {/* Gmail - requires Google connection */}
                  <button
                    className={`int-provider ${emailConfig.provider === 'gmail' ? 'active' : ''} ${!isGoogleConnected ? 'disabled' : ''}`}
                    onClick={() => {
                      if (isGoogleConnected) {
                        setEmailConfig({
                          ...emailConfig,
                          provider: 'gmail',
                          imapHost: 'imap.gmail.com',
                          imapPort: '993',
                          smtpHost: 'smtp.gmail.com',
                          smtpPort: '587',
                          email: googleConnection?.googleEmail || '',
                        });
                      }
                    }}
                    disabled={!isGoogleConnected}
                    title={!isGoogleConnected ? t('settings.email.connectGoogleFirst') : ''}
                  >
                    Gmail
                    {!isGoogleConnected && <span className="int-provider__badge">{t('settings.email.connectGoogleFirst')}</span>}
                  </button>
                  <button
                    className={`int-provider ${emailConfig.provider === 'outlook' ? 'active' : ''}`}
                    onClick={() => setEmailConfig({
                      ...emailConfig,
                      provider: 'outlook',
                      imapHost: 'outlook.office365.com',
                      imapPort: '993',
                      smtpHost: 'smtp.office365.com',
                      smtpPort: '587',
                    })}
                  >
                    Outlook
                  </button>
                  <button
                    className="int-provider disabled"
                    disabled
                    title="Coming soon"
                  >
                    Exchange
                    <span className="int-provider__badge">Soon</span>
                  </button>
                  <button
                    className={`int-provider ${emailConfig.provider === 'aruba' ? 'active' : ''}`}
                    onClick={() => setEmailConfig({
                      ...emailConfig,
                      provider: 'aruba',
                      imapHost: 'imaps.aruba.it',
                      imapPort: '993',
                      smtpHost: 'smtps.aruba.it',
                      smtpPort: '465',
                    })}
                  >
                    Aruba
                  </button>
                  <button
                    className={`int-provider ${emailConfig.provider === 'register' ? 'active' : ''}`}
                    onClick={() => setEmailConfig({
                      ...emailConfig,
                      provider: 'register',
                      imapHost: 'mail.register.it',
                      imapPort: '993',
                      smtpHost: 'mail.register.it',
                      smtpPort: '465',
                    })}
                  >
                    Register.it
                  </button>
                  <button
                    className={`int-provider ${emailConfig.provider === 'ovh' ? 'active' : ''}`}
                    onClick={() => setEmailConfig({
                      ...emailConfig,
                      provider: 'ovh',
                      imapHost: 'ssl0.ovh.net',
                      imapPort: '993',
                      smtpHost: 'ssl0.ovh.net',
                      smtpPort: '465',
                    })}
                  >
                    OVH
                  </button>
                  <button
                    className={`int-provider ${emailConfig.provider === 'custom' ? 'active' : ''}`}
                    onClick={() => setEmailConfig({
                      ...emailConfig,
                      provider: 'custom',
                    })}
                  >
                    {t('settings.email.customProvider')}
                  </button>
                </div>
              </div>

              {/* Email Configuration Form */}
              <div className="int-email-form">
                <div className="int-form-section">
                  <h4>{t('settings.email.credentials')}</h4>
                  <div className="settings-row">
                    <div className="settings-field">
                      <label className="settings-label">{t('settings.email.emailAddress')}</label>
                      <input
                        type="email"
                        className="settings-input"
                        placeholder={t('settings.email.emailPlaceholder')}
                        value={emailConfig.email}
                        onChange={(e) => setEmailConfig({...emailConfig, email: e.target.value})}
                      />
                    </div>
                    <div className="settings-field">
                      <label className="settings-label">Password</label>
                      <input
                        type="password"
                        className="settings-input"
                        placeholder={t('settings.email.passwordPlaceholder')}
                        value={emailConfig.password}
                        onChange={(e) => setEmailConfig({...emailConfig, password: e.target.value})}
                      />
                    </div>
                  </div>
                </div>

                <div className="int-form-section">
                  <h4>{t('settings.email.imapServer')}</h4>
                  <div className="settings-row">
                    <div className="settings-field">
                      <label className="settings-label">{t('settings.email.host')}</label>
                      <input
                        type="text"
                        className="settings-input"
                        placeholder={t('settings.email.imapPlaceholder')}
                        value={emailConfig.imapHost}
                        onChange={(e) => setEmailConfig({...emailConfig, imapHost: e.target.value})}
                      />
                    </div>
                    <div className="settings-field">
                      <label className="settings-label">{t('settings.email.port')}</label>
                      <input
                        type="text"
                        className="settings-input"
                        placeholder="993"
                        value={emailConfig.imapPort}
                        onChange={(e) => setEmailConfig({...emailConfig, imapPort: e.target.value})}
                      />
                    </div>
                  </div>
                </div>

                <div className="int-form-section">
                  <h4>{t('settings.email.smtpServer')}</h4>
                  <div className="settings-row">
                    <div className="settings-field">
                      <label className="settings-label">{t('settings.email.host')}</label>
                      <input
                        type="text"
                        className="settings-input"
                        placeholder={t('settings.email.smtpPlaceholder')}
                        value={emailConfig.smtpHost}
                        onChange={(e) => setEmailConfig({...emailConfig, smtpHost: e.target.value})}
                      />
                    </div>
                    <div className="settings-field">
                      <label className="settings-label">{t('settings.email.port')}</label>
                      <input
                        type="text"
                        className="settings-input"
                        placeholder="587"
                        value={emailConfig.smtpPort}
                        onChange={(e) => setEmailConfig({...emailConfig, smtpPort: e.target.value})}
                      />
                    </div>
                  </div>
                </div>

                <div className="settings-toggle-row">
                  <span>{t('settings.email.useSsl')}</span>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={emailConfig.useSsl}
                      onChange={(e) => setEmailConfig({...emailConfig, useSsl: e.target.checked})}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>

              {/* Error message */}
              {emailError && (
                <div className="int-error-message">
                  <AlertCircle size={16} />
                  <span>{emailError}</span>
                </div>
              )}

              {/* Connect button */}
              <div className="int-card__actions">
                <button
                  className="btn-primary"
                  onClick={handleEmailAction}
                  disabled={emailMode === 'testing'}
                >
                  {emailMode === 'testing' ? (
                    <><RefreshCw size={14} className="spinner" /> {t('settings.email.testingConnection')}</>
                  ) : (
                    t('settings.email.connect')
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ============================================
          CLOUD STORAGE (Dropbox)
          ============================================ */}
      {SHOW_OTHER_INTEGRATIONS && <div className={`int-box ${integrationStatus.dropbox === 'connected' ? 'int-box--connected' : ''}`}>
        <div className="int-box__header" onClick={() => togglePanel('dropbox')}>
          <div className="int-box__logo">
            <DropboxLogo />
          </div>
          <div className="int-box__info">
            <h3>Dropbox</h3>
            <p>{t('settings.email.syncFilesDocuments')}</p>
          </div>
          {getStatusBadge(integrationStatus.dropbox)}
          <ChevronDown size={20} className={`int-box__chevron ${expandedPanel === 'dropbox' ? 'expanded' : ''}`} />
        </div>

        {expandedPanel === 'dropbox' && (
          <div className="int-box__content">
            <div className="int-box__description">
              <p>
                Connect your Dropbox account to access files and attach documents
                directly from your cloud library.
              </p>
            </div>

            {integrationStatus.dropbox === 'connected' ? (
              <>
                <div className="int-box__account">
                  <div className="int-account">
                    <div className="int-account__avatar" style={{background: '#0061FF'}}>
                      <DropboxLogo />
                    </div>
                    <div className="int-account__info">
                      <span className="int-account__name">marco.rossi@email.com</span>
                      <span className="int-account__meta">2.4 GB used of 5 GB</span>
                    </div>
                  </div>
                </div>

                <div className="int-folder-select">
                  <h4>Synced folder</h4>
                  <div className="int-folder">
                    <FolderKanban size={18} />
                    <span>/Business Assistant</span>
                    <button className="btn-link">Change</button>
                  </div>
                </div>

                <div className="int-box__actions">
                  <button className="btn-danger btn-sm" onClick={() => handleOAuthDisconnect('dropbox')}>
                    Disconnect
                  </button>
                </div>
              </>
            ) : (
              <div className="int-box__actions">
                <button
                  className="btn-dropbox"
                  onClick={() => handleOAuthConnect('dropbox')}
                  disabled={integrationStatus.dropbox === 'pending'}
                >
                  {integrationStatus.dropbox === 'pending' ? (
                    <>
                      <RefreshCw size={18} className="spinner" />
                      {t('settings.email.connecting')}
                    </>
                  ) : (
                    <>
                      <DropboxLogo />
                      Connect Dropbox
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>}

      {/* ============================================
          SLACK
          ============================================ */}
      {SHOW_OTHER_INTEGRATIONS && <div className={`int-box ${integrationStatus.slack === 'connected' ? 'int-box--connected' : ''}`}>
        <div className="int-box__header" onClick={() => togglePanel('slack')}>
          <div className="int-box__logo">
            <SlackLogo />
          </div>
          <div className="int-box__info">
            <h3>Slack</h3>
            <p>Notifications and messages in workspace</p>
          </div>
          {getStatusBadge(integrationStatus.slack)}
          <ChevronDown size={20} className={`int-box__chevron ${expandedPanel === 'slack' ? 'expanded' : ''}`} />
        </div>

        {expandedPanel === 'slack' && (
          <div className="int-box__content">
            <div className="int-box__description">
              <p>
                Receive Slack notifications for new tasks, deadlines, messages and important updates.
                You can also create tasks directly from Slack.
              </p>
            </div>

            {integrationStatus.slack === 'connected' ? (
              <>
                <div className="int-box__account">
                  <div className="int-account">
                    <div className="int-account__avatar" style={{background: '#4A154B'}}>
                      <SlackLogo />
                    </div>
                    <div className="int-account__info">
                      <span className="int-account__name">Workspace: Acme Team</span>
                      <span className="int-account__meta">Channel: #business-assistant</span>
                    </div>
                  </div>
                </div>

                <div className="int-notifications-config">
                  <h4>Active notifications</h4>
                  <div className="int-notification-list">
                    <label className="int-notification-item">
                      <input type="checkbox" defaultChecked />
                      <span>New assigned tasks</span>
                    </label>
                    <label className="int-notification-item">
                      <input type="checkbox" defaultChecked />
                      <span>Upcoming deadlines</span>
                    </label>
                    <label className="int-notification-item">
                      <input type="checkbox" />
                      <span>Task comments</span>
                    </label>
                    <label className="int-notification-item">
                      <input type="checkbox" />
                      <span>Project updates</span>
                    </label>
                  </div>
                </div>

                <div className="int-box__actions">
                  <button className="btn-secondary btn-sm">Configure channel</button>
                  <button className="btn-danger btn-sm" onClick={() => handleOAuthDisconnect('slack')}>
                    Disconnect
                  </button>
                </div>
              </>
            ) : (
              <div className="int-box__actions">
                <button
                  className="btn-slack"
                  onClick={() => handleOAuthConnect('slack')}
                  disabled={integrationStatus.slack === 'pending'}
                >
                  {integrationStatus.slack === 'pending' ? (
                    <>
                      <RefreshCw size={18} className="spinner" />
                      {t('settings.email.connecting')}
                    </>
                  ) : (
                    <>
                      <SlackLogo />
                      {t('settings.slack.addToSlack')}
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>}

      {/* ============================================
          NOTION
          ============================================ */}
      {SHOW_OTHER_INTEGRATIONS && <div className={`int-box ${integrationStatus.notion === 'connected' ? 'int-box--connected' : ''}`}>
        <div className="int-box__header" onClick={() => togglePanel('notion')}>
          <div className="int-box__logo">
            <NotionLogo />
          </div>
          <div className="int-box__info">
            <h3>Notion</h3>
            <p>Sync databases and pages</p>
          </div>
          {getStatusBadge(integrationStatus.notion)}
          <ChevronDown size={20} className={`int-box__chevron ${expandedPanel === 'notion' ? 'expanded' : ''}`} />
        </div>

        {expandedPanel === 'notion' && (
          <div className="int-box__content">
            <div className="int-box__description">
              <p>
                Connect Notion to sync databases, import pages and create
                bidirectional links between your workspaces.
              </p>
            </div>

            {integrationStatus.notion === 'connected' ? (
              <>
                <div className="int-box__account">
                  <div className="int-account">
                    <div className="int-account__avatar" style={{background: '#000'}}>
                      <NotionLogo />
                    </div>
                    <div className="int-account__info">
                      <span className="int-account__name">Acme Workspace</span>
                      <span className="int-account__meta">3 databases connected</span>
                    </div>
                  </div>
                </div>

                <div className="int-box__actions">
                  <button className="btn-secondary btn-sm">Manage databases</button>
                  <button className="btn-danger btn-sm" onClick={() => handleOAuthDisconnect('notion')}>
                    Disconnect
                  </button>
                </div>
              </>
            ) : (
              <div className="int-box__actions">
                <button
                  className="btn-notion"
                  onClick={() => handleOAuthConnect('notion')}
                  disabled={integrationStatus.notion === 'pending'}
                >
                  {integrationStatus.notion === 'pending' ? (
                    <>
                      <RefreshCw size={18} className="spinner" />
                      {t('settings.email.connecting')}
                    </>
                  ) : (
                    <>
                      <NotionLogo />
                      Connect Notion
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>}
    </div>
  );
}

// =============================================================================
// INTEGRATION LOGO COMPONENTS
// =============================================================================

function GoogleLogo() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}

function MicrosoftLogo() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24">
      <rect x="1" y="1" width="10" height="10" fill="#F25022"/>
      <rect x="13" y="1" width="10" height="10" fill="#7FBA00"/>
      <rect x="1" y="13" width="10" height="10" fill="#00A4EF"/>
      <rect x="13" y="13" width="10" height="10" fill="#FFB900"/>
    </svg>
  );
}

function TeamsIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
      <path fill="#5059C9" d="M20.5 7h-6c-.83 0-1.5.67-1.5 1.5v9c0 .83.67 1.5 1.5 1.5h6c.83 0 1.5-.67 1.5-1.5v-9c0-.83-.67-1.5-1.5-1.5z"/>
      <circle fill="#5059C9" cx="19" cy="4" r="2.5"/>
      <path fill="#7B83EB" d="M14 7H4c-.83 0-1.5.67-1.5 1.5v9c0 .83.67 1.5 1.5 1.5h10c.83 0 1.5-.67 1.5-1.5v-9c0-.83-.67-1.5-1.5-1.5z"/>
      <circle fill="#7B83EB" cx="9" cy="4" r="3"/>
    </svg>
  );
}

function OneDriveIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18">
      <path fill="#0364B8" d="M14.5 18h5.25c2.35 0 4.25-1.9 4.25-4.25 0-2.04-1.44-3.75-3.36-4.16-.11-2.93-2.52-5.27-5.51-5.27-2.17 0-4.04 1.25-4.93 3.08C8.8 7.14 7.43 8.3 6.51 9.8 4.52 10.12 3 11.85 3 13.95 3 16.19 4.81 18 7.05 18h7.45z"/>
    </svg>
  );
}

function SharePointIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18">
      <circle fill="#036C70" cx="10" cy="10" r="6"/>
      <circle fill="#1A9BA1" cx="16" cy="14" r="5"/>
      <circle fill="#37C6D0" cx="10" cy="18" r="4"/>
    </svg>
  );
}

function DropboxLogo() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24">
      <path fill="#0061FF" d="M6 2l6 3.75L6 9.5 0 5.75zM18 2l6 3.75-6 3.75-6-3.75zM0 13.25L6 9.5l6 3.75-6 3.75zM18 9.5l6 3.75-6 3.75-6-3.75zM6 18.25l6-3.75 6 3.75-6 3.75z"/>
    </svg>
  );
}

function SlackLogo() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24">
      <path fill="#E01E5A" d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z"/>
      <path fill="#36C5F0" d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312z"/>
      <path fill="#2EB67D" d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312z"/>
      <path fill="#ECB22E" d="M15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
    </svg>
  );
}

function NotionLogo() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24">
      <path fill="currentColor" d="M4.459 4.208c.746.606 1.026.56 2.428.466l13.215-.793c.28 0 .047-.28-.046-.326L17.86 1.968c-.42-.326-.98-.7-2.055-.607L3.01 2.295c-.466.046-.56.28-.374.466l1.823 1.447zm.793 3.08v13.904c0 .747.373 1.027 1.214.98l14.523-.84c.841-.046.934-.56.934-1.166V6.35c0-.606-.233-.933-.746-.886l-15.177.887c-.56.047-.748.327-.748.933zm14.337.745c.093.42 0 .84-.42.888l-.7.14v10.264c-.608.327-1.168.514-1.635.514-.748 0-.935-.234-1.495-.933l-4.577-7.186v6.952l1.448.327s0 .84-1.168.84l-3.22.186c-.094-.186 0-.653.327-.746l.84-.233V9.854L7.822 9.76c-.094-.42.14-1.026.793-1.073l3.456-.233 4.764 7.279v-6.44l-1.215-.14c-.093-.513.28-.886.747-.933l3.222-.187zm-12.292-5.41l12.82-.793v1.12l-12.82.793v-1.12z"/>
    </svg>
  );
}

// =============================================================================
// SUBSCRIPTION SECTION (Piano + Team + Privilegi)
// =============================================================================

function SubscriptionSection() {
  const { t } = useTranslation();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePhone, setInvitePhone] = useState('');
  const [inviteRole, setInviteRole] = useState<UserRole>('team_member');
  const [editingMember, setEditingMember] = useState<string | null>(null);

  // Mock current user (owner/admin)
  const currentUserRole: UserRole = 'owner';

  // Mock team members
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([
    { id: '1', firstName: 'Marco', lastName: 'Rossi', email: 'marco@acme.it', role: 'owner', addedAt: '2024-01-01' },
    { id: '2', firstName: 'Laura', lastName: 'Bianchi', email: 'laura@acme.it', role: 'admin', addedAt: '2024-02-15' },
    { id: '3', firstName: 'Giuseppe', lastName: 'Verdi', email: 'giuseppe@acme.it', phone: '+39 333 1234567', role: 'team_member', addedAt: '2024-03-10' },
    { id: '4', firstName: 'Anna', lastName: 'Neri', email: 'anna@external.it', role: 'collaborator', permissions: ['view', 'comment'], addedAt: '2024-06-01' },
  ]);

  // Mock subscription
  const subscription = {
    plan: 'Business',
    status: 'active',
    seats: 10,
    usedSeats: 4,
    renewalDate: '2025-02-01',
    price: '€49/month',
  };

  const getRoleLabel = (role: UserRole) => {
    switch (role) {
      case 'owner': return t('settings.roles.owner');
      case 'admin': return t('settings.roles.admin');
      case 'team_member': return t('settings.roles.teamMember');
      case 'collaborator': return t('settings.roles.collaborator');
    }
  };

  const getRoleDescription = (role: UserRole) => {
    switch (role) {
      case 'owner': return t('settings.roleDescriptions.owner');
      case 'admin': return t('settings.roleDescriptions.admin');
      case 'team_member': return t('settings.roleDescriptions.teamMember');
      case 'collaborator': return t('settings.roleDescriptions.collaborator');
    }
  };

  const getRoleBadgeClass = (role: UserRole) => {
    switch (role) {
      case 'owner': return 'badge-owner';
      case 'admin': return 'badge-admin';
      case 'team_member': return 'badge-team';
      case 'collaborator': return 'badge-collaborator';
    }
  };

  const getPermissionIcon = (permission: Permission) => {
    switch (permission) {
      case 'view': return <Eye size={14} />;
      case 'comment': return <MessageSquare size={14} />;
      case 'edit': return <Pencil size={14} />;
    }
  };

  const handleInvite = () => {
    if (!inviteEmail || !inviteFirstName || !inviteLastName) return;
    const newMember: TeamMember = {
      id: Date.now().toString(),
      firstName: inviteFirstName,
      lastName: inviteLastName,
      email: inviteEmail,
      phone: invitePhone || undefined,
      role: inviteRole,
      permissions: inviteRole === 'collaborator' ? ['view'] : undefined,
      addedAt: new Date().toISOString().split('T')[0],
    };
    setTeamMembers([...teamMembers, newMember]);
    // Reset form
    setInviteFirstName('');
    setInviteLastName('');
    setInviteEmail('');
    setInvitePhone('');
    setInviteRole('team_member');
    setShowInviteModal(false);
  };

  const resetInviteForm = () => {
    setInviteFirstName('');
    setInviteLastName('');
    setInviteEmail('');
    setInvitePhone('');
    setInviteRole('team_member');
    setShowInviteModal(false);
  };

  const handleRoleChange = (memberId: string, newRole: UserRole) => {
    setTeamMembers(teamMembers.map(m =>
      m.id === memberId
        ? { ...m, role: newRole, permissions: newRole === 'collaborator' ? ['view'] : undefined }
        : m
    ));
  };

  const handlePermissionToggle = (memberId: string, permission: Permission) => {
    setTeamMembers(teamMembers.map(m => {
      if (m.id !== memberId || m.role !== 'collaborator') return m;
      const currentPerms = m.permissions || [];
      const newPerms = currentPerms.includes(permission)
        ? currentPerms.filter(p => p !== permission)
        : [...currentPerms, permission];
      return { ...m, permissions: newPerms.length > 0 ? newPerms : ['view'] };
    }));
  };

  const handleRemoveMember = (memberId: string) => {
    setTeamMembers(teamMembers.filter(m => m.id !== memberId));
  };

  return (
    <div className="settings-section">
      {/* Subscription Info */}
      <div className="subscription-card">
        <div className="subscription-header">
          <div>
            <h3>{subscription.plan} Plan</h3>
            <p className="subscription-status active">Active</p>
          </div>
          <div className="subscription-price">
            <span className="price">{subscription.price}</span>
          </div>
        </div>
        <div className="subscription-details">
          <div className="subscription-detail">
            <span className="label">Seats used</span>
            <span className="value">{subscription.usedSeats} / {subscription.seats}</span>
          </div>
          <div className="subscription-detail">
            <span className="label">Renewal</span>
            <span className="value">{new Date(subscription.renewalDate).toLocaleDateString(undefined)}</span>
          </div>
        </div>
        <div className="subscription-actions">
          <button className="btn-secondary btn-sm">
            <CreditCard size={14} />
            Manage billing
          </button>
          <button className="btn-primary btn-sm">Upgrade plan</button>
        </div>
      </div>

      {/* Team Management */}
      <div className="team-section">
        <div className="team-header">
          <h3>
            <Users size={18} />
            Team ({teamMembers.length})
          </h3>
          <button className="btn-primary btn-sm" onClick={() => setShowInviteModal(true)}>
            <UserPlus size={14} />
            Invite
          </button>
        </div>

        {/* Role Legend */}
        <div className="role-legend">
          <div className="role-legend-item">
            <span className={`role-badge ${getRoleBadgeClass('owner')}`}>
              <Crown size={12} />
              Owner
            </span>
            <span className="role-desc">Manages subscription</span>
          </div>
          <div className="role-legend-item">
            <span className={`role-badge ${getRoleBadgeClass('admin')}`}>Admin</span>
            <span className="role-desc">Full access</span>
          </div>
          <div className="role-legend-item">
            <span className={`role-badge ${getRoleBadgeClass('team_member')}`}>Team</span>
            <span className="role-desc">No private info</span>
          </div>
          <div className="role-legend-item">
            <span className={`role-badge ${getRoleBadgeClass('collaborator')}`}>Collaborator</span>
            <span className="role-desc">Limited access</span>
          </div>
        </div>

        {/* Team List */}
        <div className="team-list">
          {teamMembers.map((member) => (
            <div key={member.id} className="team-member">
              <div className="team-member-avatar">
                {member.avatar ? (
                  <img src={member.avatar} alt={getFullName(member)} />
                ) : (
                  <span>{member.firstName.charAt(0).toUpperCase()}</span>
                )}
              </div>
              <div className="team-member-info">
                <div className="team-member-name">
                  {getFullName(member)}
                  {member.role === 'owner' && <Crown size={14} className="owner-icon" />}
                </div>
                <div className="team-member-email">{member.email}</div>
                {member.phone && <div className="team-member-phone">{member.phone}</div>}
              </div>
              <div className="team-member-role">
                {member.role === 'owner' ? (
                  <span className={`role-badge ${getRoleBadgeClass(member.role)}`}>
                    {getRoleLabel(member.role)}
                  </span>
                ) : (
                  <select
                    className="role-select"
                    value={member.role}
                    onChange={(e) => handleRoleChange(member.id, e.target.value as UserRole)}
                  >
                    <option value="admin">Admin</option>
                    <option value="team_member">Team member</option>
                    <option value="collaborator">Collaborator</option>
                  </select>
                )}
              </div>

              {/* Permissions for collaborators */}
              {member.role === 'collaborator' && (
                <div className="team-member-permissions">
                  {(['view', 'comment', 'edit'] as Permission[]).map((perm) => (
                    <button
                      key={perm}
                      className={`permission-btn ${member.permissions?.includes(perm) ? 'active' : ''}`}
                      onClick={() => handlePermissionToggle(member.id, perm)}
                      title={t(`settings.permissions.${perm}`)}
                    >
                      {getPermissionIcon(perm)}
                    </button>
                  ))}
                </div>
              )}

              {/* Actions */}
              {member.role !== 'owner' && (
                <button
                  className="team-member-remove"
                  onClick={() => handleRemoveMember(member.id)}
                  title={t('settings.remove')}
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="modal-overlay" onClick={resetInviteForm}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <UserPlus size={20} />
                {t('settings.team.addMember', 'Add Team Member')}
              </h3>
              <button className="modal-close" onClick={resetInviteForm}>
                <X size={18} />
              </button>
            </div>
            <div className="modal-body">
              {/* Personal Info */}
              <div className="modal-section">
                <h4 className="modal-section-title">
                  <User size={16} />
                  {t('settings.sections.profile', 'Personal Info')}
                </h4>

                {/* Name Row */}
                <div className="settings-row">
                  <div className="settings-field">
                    <label className="settings-label">{t('settings.firstName', 'First Name')} *</label>
                    <input
                      type="text"
                      className="settings-input"
                      value={inviteFirstName}
                      onChange={(e) => setInviteFirstName(e.target.value)}
                      placeholder="John"
                    />
                  </div>
                  <div className="settings-field">
                    <label className="settings-label">{t('settings.lastName', 'Last Name')} *</label>
                    <input
                      type="text"
                      className="settings-input"
                      value={inviteLastName}
                      onChange={(e) => setInviteLastName(e.target.value)}
                      placeholder="Doe"
                    />
                  </div>
                </div>

                {/* Contact Row */}
                <div className="settings-row">
                  <div className="settings-field">
                    <label className="settings-label">
                      <Mail size={14} />
                      {t('common.email', 'Email')} *
                    </label>
                    <input
                      type="email"
                      className="settings-input"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="email@example.com"
                    />
                  </div>
                  <div className="settings-field">
                    <label className="settings-label">
                      <Phone size={14} />
                      {t('settings.phone', 'Phone')}
                    </label>
                    <input
                      type="tel"
                      className="settings-input"
                      value={invitePhone}
                      onChange={(e) => setInvitePhone(e.target.value)}
                      placeholder="+1 555 123 4567"
                    />
                  </div>
                </div>
              </div>

              {/* Role Selection */}
              <div className="modal-section">
                <h4 className="modal-section-title">
                  <Shield size={16} />
                  {t('settings.team.role', 'Role & Permissions')}
                </h4>

                <div className="role-selection">
                  {(['admin', 'team_member', 'collaborator'] as UserRole[]).map((role) => (
                    <label
                      key={role}
                      className={`role-option ${inviteRole === role ? 'selected' : ''}`}
                      onClick={() => setInviteRole(role)}
                    >
                      <input
                        type="radio"
                        name="role"
                        value={role}
                        checked={inviteRole === role}
                        onChange={() => setInviteRole(role)}
                      />
                      <div className="role-option-content">
                        <span className={`role-badge ${getRoleBadgeClass(role)}`}>
                          {getRoleLabel(role)}
                        </span>
                        <p className="role-option-desc">{getRoleDescription(role)}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={resetInviteForm}>
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                className="btn-primary"
                onClick={handleInvite}
                disabled={!inviteFirstName || !inviteLastName || !inviteEmail}
              >
                <UserPlus size={14} />
                {t('settings.team.sendInvite', 'Send Invite')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// IMPORTS SECTION (Importazione dati da altre piattaforme)
// =============================================================================

type ImportSource = 'teamwork' | 'asana' | 'trello' | 'jira' | 'notion';
type ImportStatus = 'idle' | 'connecting' | 'fetching' | 'preview' | 'importing' | 'completed' | 'error';

interface ImportedProject {
  id: string;
  name: string;
  description?: string;
  tasksCount: number;
  milestonesCount: number;
  selected: boolean;
}

interface ImportedTask {
  id: string;
  name: string;
  description?: string;
  status: string;
  assignee?: string;
  dueDate?: string;
  projectId: string;
}

interface ImportPreviewData {
  projects: ImportedProject[];
  tasks: ImportedTask[];
  totalProjects: number;
  totalTasks: number;
  totalMilestones: number;
  totalComments: number;
  totalFiles: number;
}

function ImportsSection() {
  const { t } = useTranslation();
  const [activeSource, setActiveSource] = useState<ImportSource | null>(null);

  const importSources = [
    {
      id: 'teamwork' as ImportSource,
      name: 'Teamwork',
      description: t('settings.integrationImports.teamwork'),
      logo: '/logos/teamwork.svg',
      available: true,
    },
    {
      id: 'asana' as ImportSource,
      name: 'Asana',
      description: t('settings.integrationImports.asana'),
      logo: '/logos/asana.svg',
      available: false,
    },
    {
      id: 'trello' as ImportSource,
      name: 'Trello',
      description: t('settings.integrationImports.trello'),
      logo: '/logos/trello.svg',
      available: false,
    },
    {
      id: 'jira' as ImportSource,
      name: 'Jira',
      description: t('settings.integrationImports.jira'),
      logo: '/logos/jira.svg',
      available: false,
    },
    {
      id: 'notion' as ImportSource,
      name: 'Notion',
      description: t('settings.integrationImports.notion'),
      logo: '/logos/notion.svg',
      available: false,
    },
  ];

  return (
    <div className="settings-section">
      <div className="imports-header">
        <div className="imports-title">
          <FolderSync size={24} />
          <div>
            <h2>{t('settings.imports.title')}</h2>
            <p>{t('settings.imports.subtitle')}</p>
          </div>
        </div>
      </div>

      {!activeSource ? (
        <div className="imports-grid">
          {importSources.map((source) => (
            <button
              key={source.id}
              className={`import-source-card ${!source.available ? 'disabled' : ''}`}
              onClick={() => source.available && setActiveSource(source.id)}
              disabled={!source.available}
            >
              <div className="import-source-icon">
                {source.id === 'teamwork' && <TeamworkLogo />}
                {source.id === 'asana' && <Database size={32} />}
                {source.id === 'trello' && <Database size={32} />}
                {source.id === 'jira' && <Database size={32} />}
                {source.id === 'notion' && <Database size={32} />}
              </div>
              <div className="import-source-info">
                <h3>{source.name}</h3>
                <p>{source.description}</p>
              </div>
              {!source.available && (
                <span className="import-source-badge">{t('settings.imports.comingSoon')}</span>
              )}
            </button>
          ))}
        </div>
      ) : (
        <div className="import-wizard">
          <button className="import-back" onClick={() => setActiveSource(null)}>
            <ChevronRight size={16} style={{ transform: 'rotate(180deg)' }} />
            {t('settings.imports.backToList')}
          </button>
          {activeSource === 'teamwork' && <TeamworkImportWizard />}
        </div>
      )}
    </div>
  );
}

// Teamwork Logo Component
function TeamworkLogo() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect width="32" height="32" rx="6" fill="#4461D7"/>
      <path d="M8 16L12 12V20L8 16Z" fill="white"/>
      <path d="M14 16L18 12V20L14 16Z" fill="white"/>
      <path d="M20 16L24 12V20L20 16Z" fill="white"/>
    </svg>
  );
}

// =============================================================================
// TEAMWORK IMPORT WIZARD
// =============================================================================

function TeamworkImportWizard() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<ImportStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<ImportPreviewData | null>(null);

  // Connection config
  const [domain, setDomain] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [projectId, setProjectId] = useState('');

  // Start the import workflow
  const handleConnect = async () => {
    if (!domain || !apiToken) {
      setError(t('settings.errors.domainAndTokenRequired'));
      return;
    }

    setStatus('connecting');
    setError(null);

    try {
      // Call the remote workflow endpoint
      const response = await fetch('/api/imports/teamwork/connect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          domain,
          apiToken,
          projectId: projectId || undefined,
        }),
      });

      if (!response.ok) {
        throw new Error(t('settings.errors.connectionFailed'));
      }

      setStatus('fetching');

      // Start fetching data
      const fetchResponse = await fetch('/api/imports/teamwork/fetch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain, apiToken, projectId }),
      });

      if (!fetchResponse.ok) {
        throw new Error(t('settings.errors.fetchError'));
      }

      const data: ImportPreviewData = await fetchResponse.json();
      setPreviewData(data);
      setStatus('preview');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.errors.unknownError'));
      setStatus('error');
    }
  };

  // Confirm and save to database
  const handleConfirmImport = async () => {
    if (!previewData) return;

    setStatus('importing');
    setError(null);

    try {
      const selectedProjects = previewData.projects.filter(p => p.selected);

      const response = await fetch('/api/imports/teamwork/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          domain,
          apiToken,
          projectIds: selectedProjects.map(p => p.id),
        }),
      });

      if (!response.ok) {
        throw new Error(t('settings.errors.saveError'));
      }

      setStatus('completed');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('settings.errors.unknownError'));
      setStatus('error');
    }
  };

  // Toggle project selection
  const toggleProjectSelection = (projectId: string) => {
    if (!previewData) return;
    setPreviewData({
      ...previewData,
      projects: previewData.projects.map(p =>
        p.id === projectId ? { ...p, selected: !p.selected } : p
      ),
    });
  };

  // Select/deselect all
  const toggleAllProjects = (selected: boolean) => {
    if (!previewData) return;
    setPreviewData({
      ...previewData,
      projects: previewData.projects.map(p => ({ ...p, selected })),
    });
  };

  return (
    <div className="import-teamwork">
      <div className="import-teamwork-header">
        <TeamworkLogo />
        <div>
          <h3>{t('settings.imports.fromTeamwork')}</h3>
          <p>{t('settings.imports.connectTeamwork')}</p>
        </div>
      </div>

      {/* Step 1: Connection */}
      {(status === 'idle' || status === 'error') && (
        <div className="import-step">
          <div className="import-step-header">
            <span className="import-step-number">1</span>
            <h4>{t('settings.imports.apiCredentials')}</h4>
          </div>

          <div className="import-form">
            <div className="settings-field">
              <label className="settings-label">
                {t('settings.imports.teamworkDomain')}
              </label>
              <div className="input-with-suffix">
                <input
                  type="text"
                  className="settings-input"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder={t('settings.imports.domainPlaceholder')}
                />
                <span className="input-suffix">{t('settings.imports.domainSuffix')}</span>
              </div>
              <p className="settings-hint">
                {t('settings.imports.domainHint')}
              </p>
            </div>

            <div className="settings-field">
              <label className="settings-label">
                {t('settings.imports.apiToken')}
              </label>
              <input
                type="password"
                className="settings-input"
                value={apiToken}
                onChange={(e) => setApiToken(e.target.value)}
                placeholder={t('settings.imports.apiTokenPlaceholder')}
              />
              <p className="settings-hint">
                {t('settings.imports.apiTokenHint')}
                <a
                  href="https://developer.teamwork.com/projects/authentication/api-keys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="settings-link"
                >
                  <ExternalLink size={12} />
                  {t('settings.imports.documentation')}
                </a>
              </p>
            </div>

            <div className="settings-field">
              <label className="settings-label">
                {t('settings.imports.projectIdOptional')}
              </label>
              <input
                type="text"
                className="settings-input"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                placeholder={t('settings.imports.projectIdPlaceholder')}
              />
              <p className="settings-hint">
                {t('settings.imports.projectIdHint')}
              </p>
            </div>

            {error && (
              <div className="import-error">
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            <button className="btn-primary" onClick={handleConnect}>
              <LinkIcon size={14} />
              {t('settings.imports.connectAndVerify')}
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Connecting */}
      {status === 'connecting' && (
        <div className="import-step">
          <div className="import-loading">
            <Loader2 size={32} className="spinner" />
            <p>{t('settings.imports.connectingToTeamwork')}</p>
          </div>
        </div>
      )}

      {/* Step 3: Fetching */}
      {status === 'fetching' && (
        <div className="import-step">
          <div className="import-loading">
            <Loader2 size={32} className="spinner" />
            <p>{t('settings.imports.fetchingData')}</p>
            <p className="import-loading-hint">
              {t('settings.imports.fetchingHint')}
            </p>
          </div>
        </div>
      )}

      {/* Step 4: Preview */}
      {status === 'preview' && previewData && (
        <div className="import-step">
          <div className="import-step-header">
            <span className="import-step-number">2</span>
            <h4>{t('settings.imports.dataPreview')}</h4>
          </div>

          {/* Summary stats */}
          <div className="import-stats">
            <div className="import-stat">
              <span className="import-stat-value">{previewData.totalProjects}</span>
              <span className="import-stat-label">{t('settings.imports.projects')}</span>
            </div>
            <div className="import-stat">
              <span className="import-stat-value">{previewData.totalTasks}</span>
              <span className="import-stat-label">{t('settings.imports.tasks')}</span>
            </div>
            <div className="import-stat">
              <span className="import-stat-value">{previewData.totalMilestones}</span>
              <span className="import-stat-label">{t('settings.imports.milestones')}</span>
            </div>
            <div className="import-stat">
              <span className="import-stat-value">{previewData.totalComments}</span>
              <span className="import-stat-label">{t('settings.imports.comments')}</span>
            </div>
            <div className="import-stat">
              <span className="import-stat-value">{previewData.totalFiles}</span>
              <span className="import-stat-label">{t('settings.imports.files')}</span>
            </div>
          </div>

          {/* Project selection */}
          <div className="import-projects">
            <div className="import-projects-header">
              <h5>{t('settings.imports.selectProjectsToImport')}</h5>
              <div className="import-projects-actions">
                <button className="btn-link" onClick={() => toggleAllProjects(true)}>
                  {t('settings.imports.selectAll')}
                </button>
                <button className="btn-link" onClick={() => toggleAllProjects(false)}>
                  {t('settings.imports.deselectAll')}
                </button>
              </div>
            </div>

            <div className="import-projects-list">
              {previewData.projects.map((project) => (
                <label key={project.id} className="import-project-item">
                  <input
                    type="checkbox"
                    checked={project.selected}
                    onChange={() => toggleProjectSelection(project.id)}
                  />
                  <div className="import-project-info">
                    <span className="import-project-name">{project.name}</span>
                    {project.description && (
                      <span className="import-project-desc">{project.description}</span>
                    )}
                    <span className="import-project-meta">
                      {project.tasksCount} task · {project.milestonesCount} milestone
                    </span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {error && (
            <div className="import-error">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          <div className="import-actions">
            <button className="btn-secondary" onClick={() => setStatus('idle')}>
              {t('settings.imports.cancel')}
            </button>
            <button
              className="btn-primary"
              onClick={handleConfirmImport}
              disabled={!previewData.projects.some(p => p.selected)}
            >
              <Download size={14} />
              {t('settings.imports.importProjects', { count: previewData.projects.filter(p => p.selected).length })}
            </button>
          </div>
        </div>
      )}

      {/* Step 5: Importing */}
      {status === 'importing' && (
        <div className="import-step">
          <div className="import-loading">
            <Loader2 size={32} className="spinner" />
            <p>{t('settings.imports.importing')}</p>
            <p className="import-loading-hint">
              {t('settings.imports.savingToDatabase')}
            </p>
          </div>
        </div>
      )}

      {/* Step 6: Completed */}
      {status === 'completed' && (
        <div className="import-step">
          <div className="import-success">
            <CheckCircle2 size={48} />
            <h4>{t('settings.imports.importComplete')}</h4>
            <p>{t('settings.imports.dataImportedSuccessfully')}</p>
            <button className="btn-primary" onClick={() => {
              setStatus('idle');
              setPreviewData(null);
              setDomain('');
              setApiToken('');
              setProjectId('');
            }}>
              {t('settings.imports.newImport')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
